import React, { useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../contexts/TranslationContext';
import { featureVisibility } from '../config/featureVisibility';
import { Globe, ChevronDown } from 'lucide-react';

const LanguageSelector = () => {
  const { language, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = useMemo(() => {
    const all = [
      { code: 'en', name: t('nav.english'), flag: '🇺🇸' },
      { code: 'mr', name: t('nav.marathi'), flag: '🇮🇳' },
    ];
    if (featureVisibility.language.showEnglish) {
      return all;
    }
    return all.filter((lang) => lang.code !== 'en');
  }, [t]);

  const currentLanguage =
    languages.find((lang) => lang.code === language) ?? languages[0];

  if (!currentLanguage) {
    return null;
  }

  if (languages.length === 1) {
    return (
      <div className="gadkot-nav__lang" aria-label={currentLanguage.name}>
        <Globe className="w-4 h-4 shrink-0" />
        <span>
          {currentLanguage.flag} {currentLanguage.name}
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="gadkot-nav__lang"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4 shrink-0" />
        <span>
          {currentLanguage.flag} {currentLanguage.name}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="gadkot-nav__lang-menu absolute top-full right-0 z-50 mt-2 w-48 overflow-hidden rounded-xl"
          role="listbox"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={language === lang.code}
              onClick={() => {
                changeLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`gadkot-nav__lang-option flex w-full items-center space-x-3 px-4 py-3 ${
                language === lang.code ? 'gadkot-nav__lang-option--active' : 'text-gray-700'
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="font-medium">{lang.name}</span>
              {language === lang.code && (
                <span className="ml-auto text-amber-700">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
